import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Palette, Eye, Check, ChevronRight, Monitor, Smartphone, 
  ArrowLeft, Sparkles, Layout, Grid3X3, Zap
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;

const EUTemplateLibrary = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const businessSlug = searchParams.get('business') || 'carlos-electricidad';
  
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewMode, setPreviewMode] = useState('mobile'); // 'mobile' ou 'desktop'
  const [business, setBusiness] = useState(null);
  const [saving, setSaving] = useState(false);

  // Imagens de preview dos templates (thumbnails)
  const templatePreviews = {
    madrid: {
      desktop: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=500&fit=crop',
      mobile: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=700&fit=crop',
      color: '#1e40af'
    },
    barcelona: {
      desktop: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&h=500&fit=crop',
      mobile: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=700&fit=crop',
      color: '#dc2626'
    }
  };

  useEffect(() => {
    loadData();
  }, [businessSlug]);

  const loadData = async () => {
    try {
      // Carrega templates
      const templatesRes = await fetch(`${API}/api/eu/templates?active_only=true`);
      if (templatesRes.ok) {
        const templatesData = await templatesRes.json();
        setTemplates(templatesData);
      }

      // Carrega business (se existir)
      try {
        const businessRes = await fetch(`${API}/api/eu/businesses/${businessSlug}`);
        if (businessRes.ok) {
          const businessData = await businessRes.json();
          setBusiness(businessData);
          setSelectedTemplate(businessData.design_settings?.template_id || 'madrid');
        }
      } catch (e) {
        // Business não existe, cria demo
        await fetch(`${API}/api/eu/demo/carlos-electricidad`, { method: 'POST' });
        const businessRes = await fetch(`${API}/api/eu/businesses/carlos-electricidad`);
        if (businessRes.ok) {
          const businessData = await businessRes.json();
          setBusiness(businessData);
          setSelectedTemplate('madrid');
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = (templateId) => {
    // Abre preview em nova aba ou modal
    navigate(`/eu/preview/${businessSlug}?template=${templateId}&mode=${previewMode}`);
  };

  const handleSelectTemplate = async (templateId) => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/api/eu/businesses/${businessSlug}/design`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ template_id: templateId })
      });
      
      if (response.ok) {
        setSelectedTemplate(templateId);
        // Redireciona para a página pública
        navigate(`/eu/${businessSlug}`);
      }
    } catch (error) {
      console.error('Error selecting template:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-slate-950/95 backdrop-blur-sm border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft size={20} />
            <span className="hidden sm:inline">Volver</span>
          </button>
          
          <div className="flex items-center gap-2">
            <Palette className="text-blue-500" size={24} />
            <span className="font-semibold text-white">Biblioteca de Diseños</span>
          </div>

          {/* Toggle Mobile/Desktop */}
          <div className="flex items-center gap-1 bg-slate-800 rounded-lg p-1">
            <button
              onClick={() => setPreviewMode('mobile')}
              className={`p-2 rounded-md transition-colors ${
                previewMode === 'mobile' 
                  ? 'bg-blue-600 text-white' 
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <Smartphone size={18} />
            </button>
            <button
              onClick={() => setPreviewMode('desktop')}
              className={`p-2 rounded-md transition-colors ${
                previewMode === 'desktop' 
                  ? 'bg-blue-600 text-white' 
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <Monitor size={18} />
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-blue-500/10 text-blue-400 px-4 py-2 rounded-full text-sm mb-6">
            <Sparkles size={16} />
            <span>8 diseños profesionales</span>
          </div>
          
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            Elige el diseño de tu página
          </h1>
          
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            Puedes cambiarlo cuando quieras sin perder tu información. 
            Todos los diseños están optimizados para móvil y escritorio.
          </p>
        </div>
      </section>

      {/* Templates Grid */}
      <section className="pb-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {templates.map((template) => {
              const isSelected = selectedTemplate === template.id;
              const preview = templatePreviews[template.id] || templatePreviews.madrid;
              
              return (
                <div 
                  key={template.id}
                  className={`group relative bg-slate-900 rounded-2xl overflow-hidden border-2 transition-all duration-300 ${
                    isSelected 
                      ? 'border-blue-500 shadow-lg shadow-blue-500/20' 
                      : 'border-slate-800 hover:border-slate-700'
                  }`}
                >
                  {/* Badge de selecionado */}
                  {isSelected && (
                    <div className="absolute top-4 right-4 z-10">
                      <Badge className="bg-blue-600 text-white gap-1">
                        <Check size={14} />
                        Actual
                      </Badge>
                    </div>
                  )}

                  {/* Preview Image */}
                  <div className="relative aspect-[4/3] bg-slate-800 overflow-hidden">
                    {/* Thumbnail mockup */}
                    <div className="absolute inset-0 flex items-center justify-center p-6">
                      {previewMode === 'mobile' ? (
                        <div 
                          className="w-48 h-80 rounded-3xl overflow-hidden shadow-2xl border-4 border-slate-700"
                          style={{ backgroundColor: preview.color }}
                        >
                          <div className="h-full bg-white/10 backdrop-blur-sm p-3">
                            <div className="w-full h-8 bg-white/20 rounded-lg mb-3" />
                            <div className="w-3/4 h-4 bg-white/30 rounded mb-2" />
                            <div className="w-1/2 h-3 bg-white/20 rounded mb-4" />
                            <div className="flex gap-2 mb-4">
                              <div className="flex-1 h-8 bg-white/40 rounded-lg" />
                              <div className="flex-1 h-8 bg-white/20 rounded-lg" />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              {[1,2,3,4].map(i => (
                                <div key={i} className="aspect-square bg-white/15 rounded-lg" />
                              ))}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div 
                          className="w-full max-w-md h-56 rounded-lg overflow-hidden shadow-2xl border border-slate-700"
                          style={{ backgroundColor: preview.color }}
                        >
                          <div className="h-full bg-white/10 backdrop-blur-sm p-4">
                            <div className="flex items-center justify-between mb-4">
                              <div className="w-20 h-6 bg-white/30 rounded" />
                              <div className="flex gap-2">
                                {[1,2,3].map(i => (
                                  <div key={i} className="w-12 h-4 bg-white/20 rounded" />
                                ))}
                              </div>
                            </div>
                            <div className="flex gap-4">
                              <div className="flex-1">
                                <div className="w-3/4 h-6 bg-white/30 rounded mb-2" />
                                <div className="w-1/2 h-4 bg-white/20 rounded mb-4" />
                                <div className="flex gap-2">
                                  <div className="w-24 h-8 bg-white/40 rounded-lg" />
                                  <div className="w-20 h-8 bg-white/20 rounded-lg" />
                                </div>
                              </div>
                              <div className="w-32 h-28 bg-white/15 rounded-lg" />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Overlay on hover */}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4">
                      <Button
                        onClick={() => handlePreview(template.id)}
                        variant="outline"
                        className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                      >
                        <Eye size={18} className="mr-2" />
                        Vista previa
                      </Button>
                    </div>
                  </div>

                  {/* Info */}
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-xl font-bold text-white mb-1">{template.name}</h3>
                        <p className="text-slate-400 text-sm">{template.description}</p>
                      </div>
                      <Badge variant="outline" className="text-slate-400 border-slate-700">
                        {template.style}
                      </Badge>
                    </div>

                    {/* Features */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {template.features?.slice(0, 3).map((feature, idx) => (
                        <span 
                          key={idx}
                          className="text-xs px-2 py-1 bg-slate-800 text-slate-400 rounded"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3">
                      <Button
                        onClick={() => handlePreview(template.id)}
                        variant="outline"
                        className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800"
                      >
                        <Eye size={16} className="mr-2" />
                        Vista previa
                      </Button>
                      <Button
                        onClick={() => handleSelectTemplate(template.id)}
                        disabled={saving || isSelected}
                        className={`flex-1 ${
                          isSelected 
                            ? 'bg-green-600 hover:bg-green-600' 
                            : 'bg-blue-600 hover:bg-blue-700'
                        }`}
                      >
                        {isSelected ? (
                          <>
                            <Check size={16} className="mr-2" />
                            Seleccionado
                          </>
                        ) : (
                          <>
                            Elegir
                            <ChevronRight size={16} className="ml-2" />
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Próximos templates (desativados) */}
          <div className="mt-12">
            <h2 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
              <Grid3X3 className="text-slate-500" size={20} />
              Próximamente
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
              {['Valencia', 'Sevilla', 'Málaga', 'Bilbao', 'Granada', 'Ibiza'].map((name) => (
                <div 
                  key={name}
                  className="aspect-square bg-slate-900/50 rounded-xl border border-slate-800 flex flex-col items-center justify-center text-slate-600"
                >
                  <Layout size={24} className="mb-2 opacity-50" />
                  <span className="text-sm">{name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EUTemplateLibrary;
